import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { InputActionMeta } from "react-select";
import { InputSelect } from "@/shared/components/form/InputSelect";
import { Button } from "@/shared/components/form/Button";
import type { NewConfirmationFormData } from "../../confirmations/types/new-confirmation.types";
import type { TreeClientListItem } from "../../clients/types/clients-api.types";
import useTranslations from "@/shared/lang/useTranslations";
import { useDebouncedVerifiedClientSearch } from "../hooks/useDebouncedVerifiedClientSearch";
import {
  paymentOrganizationTreeLabel,
  paymentSubclientDisplayName,
} from "../utils/paymentClientDisplayName";

type SelectOption = { label: string; value: string };

function payerAccountIdsFromSelection(
  row: TreeClientListItem | null,
  pagadorId: string | null
): string[] {
  if (!row || !pagadorId) return [];
  if (row.id === pagadorId) {
    return row.accountIds ?? [];
  }
  const sub = row.clients?.find((s) => s.id === pagadorId);
  return sub?.accountIds ?? [];
}

type PaymentClientPayerFieldsProps = {
  value: NewConfirmationFormData;
  onChange: (data: NewConfirmationFormData) => void;
  errors?: Partial<Record<keyof NewConfirmationFormData, string>>;
  disabled?: boolean;
  fetchEnabled: boolean;
  onPayerAccountIdsChange?: (accountIds: string[]) => void;
};

export function PaymentClientPayerFields({
  value,
  onChange,
  errors = {},
  disabled = false,
  fetchEnabled,
  onPayerAccountIdsChange,
}: PaymentClientPayerFieldsProps) {
  const { t: tConf } = useTranslations("confirmations");
  const { t: tPay } = useTranslations("payments");
  const navigate = useNavigate();

  const [clientMenuInput, setClientMenuInput] = useState("");
  const [selectedClientRow, setSelectedClientRow] = useState<TreeClientListItem | null>(
    null
  );

  const {
    clients: searchResults,
    isLoading: isClientSearchLoading,
    error: loadError,
    debouncedSearch,
  } = useDebouncedVerifiedClientSearch(fetchEnabled, clientMenuInput);

  const showClientSearchEmpty =
    fetchEnabled &&
    debouncedSearch.trim().length > 0 &&
    !isClientSearchLoading &&
    !loadError &&
    searchResults.length === 0;

  useEffect(() => {
    if (!fetchEnabled) {
      setClientMenuInput("");
      setSelectedClientRow(null);
    }
  }, [fetchEnabled]);

  useEffect(() => {
    if (!value.clienteId) {
      setSelectedClientRow(null);
    }
  }, [value.clienteId]);

  useEffect(() => {
    if (!onPayerAccountIdsChange) return;
    if (!fetchEnabled) {
      onPayerAccountIdsChange([]);
      return;
    }
    onPayerAccountIdsChange(
      payerAccountIdsFromSelection(selectedClientRow, value.pagadorId)
    );
  }, [
    fetchEnabled,
    onPayerAccountIdsChange,
    selectedClientRow,
    value.pagadorId,
  ]);

  const clientOptions: SelectOption[] = useMemo(() => {
    const fromApi = searchResults.map((c) => ({
      label: paymentOrganizationTreeLabel(c) || c.id,
      value: c.id,
    }));
    if (
      selectedClientRow &&
      !fromApi.some((o) => o.value === selectedClientRow.id)
    ) {
      return [
        {
          label: paymentOrganizationTreeLabel(selectedClientRow) || selectedClientRow.id,
          value: selectedClientRow.id,
        },
        ...fromApi,
      ];
    }
    return fromApi;
  }, [searchResults, selectedClientRow]);

  const subclients = selectedClientRow?.clients ?? [];
  const hasSubclients = subclients.length > 0;

  /** Misma fila que Cliente: siempre opciones de select; sin subclientes, una sola opción (el cliente). */
  const payerOptions: SelectOption[] = useMemo(() => {
    if (!selectedClientRow) return [];
    const subs = selectedClientRow.clients ?? [];
    const clientOption: SelectOption = {
      label: paymentOrganizationTreeLabel(selectedClientRow) || selectedClientRow.id,
      value: selectedClientRow.id,
    };
    if (subs.length > 0) {
      return [
        clientOption,
        ...subs.map((s) => ({
          label: paymentSubclientDisplayName(s) || s.id,
          value: s.id,
        })),
      ];
    }
    return [clientOption];
  }, [selectedClientRow]);

  const clientValue =
    clientOptions.find((o) => o.value === value.clienteId) ?? null;
  const payerValue =
    payerOptions.find((o) => o.value === value.pagadorId) ?? null;

  const handleClientInputChange = useCallback(
    (input: string, meta: InputActionMeta) => {
      if (meta.action === "input-change") {
        setClientMenuInput(input);
      }
    },
    []
  );

  const handleClientChange = (opt: SelectOption | null) => {
    setClientMenuInput("");
    if (!opt) {
      setSelectedClientRow(null);
      onChange({
        ...value,
        cliente: "",
        clienteId: null,
        nombrePagador: "",
        pagadorId: null,
        pagadorEsMismoCliente: false,
      });
      return;
    }
    const row =
      searchResults.find((c) => c.id === opt.value) ??
      (selectedClientRow?.id === opt.value ? selectedClientRow : null);
    if (!row) return;
    setSelectedClientRow(row);
    const display = paymentOrganizationTreeLabel(row) || row.id;
    const subs = row.clients ?? [];
    if (subs.length === 0) {
      onChange({
        ...value,
        cliente: display,
        clienteId: row.id,
        nombrePagador: display,
        pagadorId: row.id,
        pagadorEsMismoCliente: true,
      });
    } else {
      onChange({
        ...value,
        cliente: display,
        clienteId: row.id,
        nombrePagador: "",
        pagadorId: null,
        pagadorEsMismoCliente: false,
      });
    }
  };

  const handlePayerChange = (opt: SelectOption | null) => {
    if (!opt) {
      onChange({
        ...value,
        nombrePagador: "",
        pagadorId: null,
        pagadorEsMismoCliente: false,
      });
      return;
    }
    if (hasSubclients) {
      if (!selectedClientRow) return;
      if (opt.value === selectedClientRow.id) {
        const display =
          paymentOrganizationTreeLabel(selectedClientRow) || selectedClientRow.id;
        onChange({
          ...value,
          nombrePagador: display,
          pagadorId: selectedClientRow.id,
          pagadorEsMismoCliente: true,
        });
        return;
      }
      const sub = subclients.find((s) => s.id === opt.value);
      if (!sub) return;
      onChange({
        ...value,
        nombrePagador: paymentSubclientDisplayName(sub) || sub.id,
        pagadorId: sub.id,
        pagadorEsMismoCliente: false,
      });
      return;
    }
    if (!selectedClientRow || opt.value !== selectedClientRow.id) return;
    const display =
      paymentOrganizationTreeLabel(selectedClientRow) || selectedClientRow.id;
    onChange({
      ...value,
      nombrePagador: display,
      pagadorId: selectedClientRow.id,
      pagadorEsMismoCliente: true,
    });
  };

  return (
    <div className="space-y-4">
      {loadError && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
          {loadError}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 items-start">
        <div className="space-y-2 min-w-0">
          <InputSelect<SelectOption>
            name="payment-form-cliente"
            label={tConf("form_label_client")}
            options={clientOptions}
            value={clientValue}
            onChange={(v) => handleClientChange((Array.isArray(v) ? v[0] : v) ?? null)}
            onInputChange={handleClientInputChange}
            filterOption={() => true}
            placeholder={tConf("form_placeholder_client")}
            isDisabled={disabled}
            isLoading={isClientSearchLoading}
            isClearable
            errorMessage={errors.cliente}
            maxWidth="max-w-full"
            controlClassName="!h-12"
            noOptionsMessage={() => tPay("form_client_search_dropdown_empty")}
          />
        </div>

        <div className="space-y-2 min-w-0">
          <InputSelect<SelectOption>
            name="payment-form-pagador"
            label={tConf("form_label_beneficiary")}
            options={payerOptions}
            value={payerValue}
            onChange={(v) => handlePayerChange((Array.isArray(v) ? v[0] : v) ?? null)}
            placeholder={
              hasSubclients
                ? tPay("form_payer_subclient_placeholder")
                : tConf("form_placeholder_beneficiary")
            }
            isDisabled={disabled || !value.clienteId}
            isLoading={false}
            isClearable={hasSubclients}
            errorMessage={errors.nombrePagador}
            maxWidth="max-w-full"
            controlClassName="!h-12"
          />
        </div>

        {showClientSearchEmpty && (
          <div
            className="md:col-span-2 rounded-lg border border-amber-200/90 dark:border-amber-800/60 bg-amber-50/90 dark:bg-amber-950/25 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 sm:gap-4 text-sm text-zinc-700 dark:text-zinc-300"
            role="status"
          >
            <p className="leading-snug flex-1 min-w-0">
              {tPay("form_client_search_empty_message")}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/app/clients")}
              className="shrink-0 self-start sm:self-center text-sm py-1.5 px-3"
            >
              {tPay("form_client_search_go_to_clients")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
